import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Save, Trash2, Eye, EyeOff, ArrowUpDown, MessageSquareQuote, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

interface Testimonial {
  id: string;
  author_name: string;
  handle: string;
  platform: string;
  tag: string;
  quote: string;
  email?: string | null;
  avatar_initials: string | null;
  sort_order: number;
  active: boolean;
  status: "approved" | "pending";
  created_at: string;
  updated_at: string;
}

const PLATFORMS = ["YouTube","Instagram","Facebook","LinkedIn","TikTok","X","Threads","Pinterest","Reddit","Discord","Telegram","Bluesky","Website"];
const TAGS = ["Lead", "Complaint solved", "Question", "Praise"];

const emptyForm = {
  author_name: "", handle: "", platform: "Instagram", tag: "Praise",
  quote: "", avatar_initials: "", sort_order: 100, active: true,
};

const AdminTestimonials: React.FC = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Testimonial | null>(null);
  const [form, setForm] = useState({ ...emptyForm });

  const q = useQuery<Testimonial[]>({
    queryKey: ["admin-testimonials"],
    queryFn: async () => (await api.get("/admin/testimonials")).data,
  });

  const openNew = () => { setEditing(null); setForm({ ...emptyForm }); setOpen(true); };
  const openEdit = (t: Testimonial) => {
    setEditing(t);
    setForm({
      author_name: t.author_name, handle: t.handle, platform: t.platform,
      tag: t.tag, quote: t.quote, avatar_initials: t.avatar_initials || "",
      sort_order: t.sort_order, active: t.active,
    });
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form, avatar_initials: form.avatar_initials || null };
      if (editing) return (await api.put(`/admin/testimonials/${editing.id}`, body)).data;
      return (await api.post("/admin/testimonials", body)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Testimonial updated" : "Testimonial added");
      qc.invalidateQueries({ queryKey: ["admin-testimonials"] });
      setOpen(false);
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const toggleActive = useMutation({
    mutationFn: async (t: Testimonial) =>
      (await api.put(`/admin/testimonials/${t.id}`, { active: !t.active })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-testimonials"] }),
  });

  const approve = useMutation({
    mutationFn: async (t: Testimonial) =>
      (await api.post(`/admin/testimonials/${t.id}/approve`)).data,
    onSuccess: () => {
      toast.success("Testimonial approved and live");
      qc.invalidateQueries({ queryKey: ["admin-testimonials"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Approve failed"),
  });

  const del = useMutation({
    mutationFn: async (t: Testimonial) => (await api.delete(`/admin/testimonials/${t.id}`)).data,
    onSuccess: () => {
      toast.success("Testimonial deleted");
      qc.invalidateQueries({ queryKey: ["admin-testimonials"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  return (
    <DashboardLayout
      title="Wall of Comments"
      subtitle="Curate the testimonials shown on your public landing page."
      seoPath="/admin/testimonials"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-[color:var(--sg-muted)]">
          Only <b className="text-[color:var(--sg-fg)]">active</b> testimonials appear on the landing page.
          Lowest <b className="text-[color:var(--sg-fg)]">sort_order</b> displays first.
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-testimonial-btn"
              className="bg-[color:var(--sg-fg)] text-white hover:bg-black">
              <Plus className="h-4 w-4 mr-1" /> Add testimonial
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit testimonial" : "Add testimonial"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Author name</Label>
                  <Input data-testid="tst-name" value={form.author_name}
                    onChange={(e) => setForm({ ...form, author_name: e.target.value })} /></div>
                <div><Label>Handle</Label>
                  <Input data-testid="tst-handle" placeholder="@username" value={form.handle}
                    onChange={(e) => setForm({ ...form, handle: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Platform</Label>
                  <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                    <SelectTrigger data-testid="tst-platform"><SelectValue /></SelectTrigger>
                    <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Tag</Label>
                  <Select value={form.tag} onValueChange={(v) => setForm({ ...form, tag: v })}>
                    <SelectTrigger data-testid="tst-tag"><SelectValue /></SelectTrigger>
                    <SelectContent>{TAGS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div><Label>Quote</Label>
                <Textarea data-testid="tst-quote" rows={4} value={form.quote}
                  onChange={(e) => setForm({ ...form, quote: e.target.value })}
                  placeholder="What are they saying about SociBaba?" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Sort order</Label>
                  <Input data-testid="tst-order" type="number" value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} /></div>
                <div><Label>Avatar initials (opt.)</Label>
                  <Input data-testid="tst-initials" maxLength={2} value={form.avatar_initials}
                    onChange={(e) => setForm({ ...form, avatar_initials: e.target.value.toUpperCase() })}
                    placeholder="e.g. RK" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm mt-1">
                <input type="checkbox" data-testid="tst-active" checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Active (show on landing)
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="tst-save"
                disabled={save.isPending || !form.author_name || !form.handle || !form.quote}
                onClick={() => save.mutate()}
                className="bg-[color:var(--sg-fg)] text-white hover:bg-black">
                {save.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="testimonials-grid">
        {q.isLoading && <div className="col-span-full flex items-center gap-2 text-[color:var(--sg-muted)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {q.data && q.data.length === 0 && (
          <div className="col-span-full text-center py-10 text-sm text-[color:var(--sg-muted)]">
            No testimonials yet — click &ldquo;Add testimonial&rdquo; to create one.
          </div>
        )}
        {q.data?.map((t) => {
          const initials = t.avatar_initials || (t.author_name || t.handle).split(/\s+/).slice(0,2).map((w) => w[0]?.toUpperCase()).join("");
          return (
            <div key={t.id} className={`sg-card p-5 flex flex-col ${!t.active ? "opacity-60" : ""} ${t.status === "pending" ? "ring-2 ring-amber-400" : ""}`} data-testid={`tst-row-${t.id}`}>
              {t.status === "pending" && (
                <div className="mb-3 -mt-1 flex items-center gap-2 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-1 w-fit">
                  <Clock className="h-3 w-3" /> Pending review {t.email ? `· ${t.email}` : ""}
                </div>
              )}
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-full bg-[color:var(--sg-fg)] text-white text-xs font-bold flex items-center justify-center">
                  {initials || "•"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[color:var(--sg-fg)]">{t.author_name}</div>
                  <div className="text-[11px] text-[color:var(--sg-muted)]">{t.handle} · {t.platform}</div>
                </div>
                <span className={`sg-chip text-[10px] ${
                  t.tag === "Lead" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                  t.tag === "Complaint solved" ? "bg-red-100 text-red-800 border-red-200" :
                  t.tag === "Question" ? "bg-amber-100 text-amber-800 border-amber-200" :
                  "bg-slate-100 text-slate-700 border-slate-200"
                }`}>{t.tag}</span>
              </div>
              <MessageSquareQuote className="h-4 w-4 text-[color:var(--sg-muted)] mb-1" />
              <div className="text-sm text-[color:var(--sg-fg)] leading-relaxed flex-1">
                {t.quote}
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-[color:var(--sg-border)]">
                <div className="flex items-center gap-1 text-[11px] text-[color:var(--sg-muted)]">
                  <ArrowUpDown className="h-3 w-3" /> {t.sort_order}
                </div>
                <div className="flex items-center gap-1">
                  {t.status === "pending" ? (
                    <Button size="sm"
                      onClick={() => approve.mutate(t)}
                      className="h-7 text-xs bg-emerald-600 text-white hover:bg-emerald-700"
                      data-testid={`tst-approve-${t.id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => toggleActive.mutate(t)} className="h-7 text-xs" data-testid={`tst-toggle-${t.id}`}>
                      {t.active ? <><Eye className="h-3 w-3 mr-1" /> Live</> : <><EyeOff className="h-3 w-3 mr-1" /> Hidden</>}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(t)} className="h-7 text-xs" data-testid={`tst-edit-${t.id}`}>
                    Edit
                  </Button>
                  <Button size="sm" variant="outline"
                    onClick={() => window.confirm("Delete this testimonial?") && del.mutate(t)}
                    className="h-7 text-xs text-red-600 hover:bg-red-50 border-red-200" data-testid={`tst-delete-${t.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
};

export default AdminTestimonials;
